import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MenuItem,
  ListItemIcon,
  useTheme,
  Divider,
  IconButton,
  Checkbox,
  TextField,
  Tooltip,
  Grid,
  Chip,
  Menu,
  ListItemText,
  Popover,
  TablePagination,
  Switch,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  SearchRounded,
  CloseRounded,
  ArrowDropDownRounded,
  ManageSearchRounded,
  FilterListRounded,
  ChevronRightRounded,
  ViewStreamRounded,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { getFileIconImage } from "../../../../../utils/fileUtils";
import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import Loader from "../../../../../components/Loader";
import {
  getCategoryFileCount,
  getFilesByStatus,
} from "../../../../../core/repo/fileRepo";
import debounce from "lodash/debounce";
import {
  CLICK_REFINE_SEARCH_BUTTON,
  CLICK_REFINE_SEARCH_WITHIN_SPECIFIC_FILES,
  CLICK_APPLY_SEARCH_WITHIN_FILE_CATEGORIES,
  CLICK_CANCEL_SEARCH_WITHIN_FILE_CATEGORIES,
  SELECT_FILE_CATEGORY_REFINE_SEARCH,
  SEARCH_FILES_REFINE_SEARCH,
  TOGGLE_PREVIEW_REFINE_SEARCH,
  PAGE_CHANGE_REFINE_SEARCH,
  CLICK_CLEAR_ALL_BUTTON_REFINE_SEARCH,
  CLICK_APPLY_REFINE_SEARCH,
  CLICK_CANCEL_REFINE_SEARCH,
} from "../../../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const useStyles = makeStyles()((theme, _params, classes) => ({
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  fileCountChip: {
    fontWeight: 500,
    borderRadius: 4,
  },
  refineSearchChip: {
    color: theme.palette.text.primary,
    "& .MuiChip-icon": {
      color: theme.palette.text.primary,
    },
  },
  activeRefineSearchChip: {
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.primary.main : "#E1BEE7",
    color: theme.palette.mode === "dark" ? "#092728" : "#9C27B0",
    "& .MuiChip-icon": {
      color: theme.palette.mode === "dark" ? "#092728" : "#9C27B0",
    },
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark" ? theme.palette.primary.main : "none",
    },
  },
  fileFilterChip: {
    color: theme.palette.text.secondary,
  },
  activeFileFilterChip: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    backgroundColor: theme.palette.selection,
  },
}));

export const analysisAndRetrieveAgents = [
  { name: "Data Analysis", icon: "TimelineRounded", color: "#4CAF50" },
];

export default function FilesFilterDialog({
  addTaggedFiles,
  taggedFiles,
  taggedCategories,
  addTaggedCategories,
}) {
  const posthog = usePostHog();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { selectedDomain } = useSelector((state) => state.domain);
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState(taggedFiles);
  const [query, setQuery] = useState("");
  const [filesFilterDialogOpen, setFilesFilterDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [categoryFileCount, setCategoryFileCount] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFileCategory, setSelectedFileCategory] = useState("All");
  const [filesData, setFilesData] = useState(null);
  const [page, setPage] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setSelectedFiles(taggedFiles);
    setSelectedCategories(taggedCategories);
  }, [taggedFiles, taggedCategories]);

  useEffect(() => {
    fetchData();
  }, [selectedDomain]);

  const fetchFilesData = async (category, page, query) => {
    try {
      const filesData = await dispatch(
        getFilesByStatus(
          "",
          category === "All" ? "" : category,
          page,
          selectedDomain.id,
          query
        )
      );
      setFilesData({ ...filesData, files: filesData?.files || [] });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    try {
      const categoryFileCount = await dispatch(
        getCategoryFileCount(selectedDomain.id)
      );
      setCategoryFileCount(categoryFileCount);
      fetchFilesData("", 1, "");
    } catch (error) {
      console.error(error);
    }
  };

  // Create a debounced function
  const debouncedFetchFilesData = useRef(
    debounce((query, category, page) => {
      fetchFilesData(category, page, query);
      setPage(0);
      posthog.capture(SEARCH_FILES_REFINE_SEARCH, {
        query: query,
      });
    }, 400)
  ).current;

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedFetchFilesData(value, selectedFileCategory, 1);
  };

  const handleUnselectAllFiles = () => {
    posthog.capture(CLICK_CLEAR_ALL_BUTTON_REFINE_SEARCH);
    setSelectedFiles([]);
  };

  const handleCategoryCheckbox = (isSelected, category) => {
    if (!isSelected) {
      handleSelectCategory(category);
    } else {
      handleUnselectCategory(category);
    }
  };

  const handleSelectCategory = (category) => {
    setSelectedCategories([...selectedCategories, category]);
  };

  const handleUnselectCategory = (category) => {
    setSelectedCategories(selectedCategories.filter((c) => c !== category));
  };

  const handleFileCheckbox = (isSelected, file) => {
    if (!isSelected) {
      handleSelectFile(file);
    } else {
      handleUnselectFile(file);
    }
  };

  const handleSelectFile = (file) => {
    setSelectedFiles([...selectedFiles, file]);
  };

  const handleUnselectFile = (file) => {
    setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id));
  };

  const handleClose = (source) => {
    posthog.capture(
      source === "refine-search"
        ? CLICK_CANCEL_REFINE_SEARCH
        : CLICK_CANCEL_SEARCH_WITHIN_FILE_CATEGORIES
    );
    setFilesFilterDialogOpen(false);
    setAnchorEl(null);
  };

  const handleSelectButton = (source) => {
    addTaggedFiles(selectedFiles);
    addTaggedCategories(selectedCategories);
    handleClose();
    posthog.capture(
      source === "refine-search"
        ? CLICK_APPLY_REFINE_SEARCH
        : CLICK_APPLY_SEARCH_WITHIN_FILE_CATEGORIES
    );
  };

  const handleFileCategoryChange = (category) => {
    posthog.capture(SELECT_FILE_CATEGORY_REFINE_SEARCH, {
      category: category,
    });
    setSelectedFileCategory(category);
    fetchFilesData(category, page + 1, "");
  };

  const handleChangePage = async (event, newPage) => {
    posthog.capture(PAGE_CHANGE_REFINE_SEARCH, {
      page: newPage,
    });
    setPage(newPage);
    fetchFilesData(selectedFileCategory, newPage + 1, query);
  };

  useEffect(() => {
    return () => {
      debouncedFetchFilesData.cancel();
    };
  }, [debouncedFetchFilesData]);

  const handleClickRefineSearch = (e) => {
    posthog.capture(CLICK_REFINE_SEARCH_BUTTON);
    setAnchorEl(e.currentTarget);
  };

  const handleClickRefineSearchWithinSpecificFiles = () => {
    posthog.capture(CLICK_REFINE_SEARCH_WITHIN_SPECIFIC_FILES);
    setFilesFilterDialogOpen(true);
  };

  const handleTogglePreview = () => {
    posthog.capture(TOGGLE_PREVIEW_REFINE_SEARCH, {
      preview: !showPreview,
    });
    setShowPreview(!showPreview);
  };

  return (
    <>
      <Chip
        className={
          taggedFiles.length > 0 || taggedCategories.length > 0
            ? classes.activeRefineSearchChip
            : classes.refineSearchChip
        }
        label="Refine Search"
        icon={<ManageSearchRounded />}
        onClick={handleClickRefineSearch}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              paddingBottom: 1,
              mt: -1,
              borderRadius: 4,
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.20), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
            },
          },
        }}>
        <Box>
          <Box mt={1}>
            <MenuItem onClick={handleClickRefineSearchWithinSpecificFiles}>
              <ListItemIcon>
                <FilterListRounded />
              </ListItemIcon>
              <Box display={"flex"} alignItems={"center"} gap={0.5}>
                <ListItemText>Refine search within specific files</ListItemText>
                <ChevronRightRounded
                  sx={{ color: theme.palette.text.secondary }}
                />
              </Box>
            </MenuItem>
          </Box>
          <Divider sx={{ marginY: 1 }} />
          <Box
            px={2}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            mb={1}>
            <Typography
              variant="caption"
              color={"textSecondary"}
              sx={{
                letterSpacing: "1.5px",
                fontWeight: 500,
                textTransform: "uppercase",
              }}>
              Search within File Categories
            </Typography>
          </Box>
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {categoryFileCount &&
              Object.keys(categoryFileCount)
                .sort((a, b) =>
                  a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)
                )
                .map((category) => {
                  const isSelected = selectedCategories.includes(category);
                  return (
                    category !== "All" && (
                      <MenuItem
                        key={category}
                        onClick={() =>
                          handleCategoryCheckbox(isSelected, category)
                        }>
                        <ListItemIcon>
                          <Checkbox
                            sx={{
                              padding: 0,
                            }}
                            checked={isSelected}
                          />
                        </ListItemIcon>
                        <Box display={"flex"} alignItems={"center"} gap={1.5}>
                          <ListItemText>{category}</ListItemText>
                          <Chip
                            size="small"
                            label={categoryFileCount[category]}
                            className={classes.fileCountChip}
                          />
                        </Box>
                      </MenuItem>
                    )
                  );
                })}
          </Box>
          <Box
            display={"flex"}
            gap={1}
            my={1}
            mx={2}
            justifyContent="flex-end"
            sx={{
              pointerEvents: selectedCategories.length === 0 ? "none" : "auto",
              opacity: selectedCategories.length === 0 ? 0.5 : 1,
            }}>
            <Button size="small" variant="text" onClick={handleClose}>
              <span className={classes.gradientText}>Cancel</span>
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSelectButton}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
      <Dialog
        open={filesFilterDialogOpen}
        onClose={() => setFilesFilterDialogOpen(false)}
        fullWidth
        maxWidth={showPreview ? "lg" : "md"}>
        <DialogTitle component={Box} sx={{ padding: 0 }}>
          <Box
            px={3}
            py={2}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}>
            <Typography variant="h6">Search within Specific Files</Typography>
            <IconButton onClick={() => setFilesFilterDialogOpen(false)}>
              <CloseRounded />
            </IconButton>
          </Box>
          <Divider />
          <Box
            maxHeight={100}
            overflow={"auto"}
            p={2}
            display={"flex"}
            alignItems={"center"}
            flexWrap={"wrap"}
            gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Filter by File Category:
            </Typography>
            {categoryFileCount &&
              Object.keys(categoryFileCount)
                .sort((a, b) =>
                  a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)
                )
                .map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    className={
                      category === selectedFileCategory
                        ? classes.activeFileFilterChip
                        : classes.fileFilterChip
                    }
                    onClick={() => handleFileCategoryChange(category)}
                  />
                ))}
          </Box>
          <Box px={2} pb={2} display={"flex"} alignItems={"center"} gap={1}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search files"
              size={"small"}
              value={query}
              onChange={handleQueryChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <SearchRounded
                      sx={{ mr: 1, color: theme.palette.action.active }}
                    />
                  ),
                  style: { borderRadius: 8 },
                },
              }}
            />
            <Box display="flex" alignItems="center" gap={0.5}>
              <ViewStreamRounded
                sx={{
                  color: showPreview
                    ? theme.palette.primary.main
                    : theme.palette.action.active,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Preview
              </Typography>
              <Switch
                size="small"
                checked={showPreview}
                onChange={handleTogglePreview}
                inputProps={{ "aria-label": "toggle preview" }}
              />
            </Box>
          </Box>
          {selectedFiles.length > 0 && (
            <Box
              display={"flex"}
              alignItems={"center"}
              flexWrap={"wrap"}
              gap={1}
              px={2}
              pb={2}
              maxHeight={80}
              overflow={"auto"}>
              {selectedFiles.map((file, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={file.name}
                  icon={
                    <img
                      src={getFileIconImage(file.file_type)}
                      alt="file_icon"
                      width={16}
                      height={16}
                      style={{ borderRadius: "50%" }}
                    />
                  }
                  onDelete={() => handleUnselectFile(file)}
                  sx={{
                    fontWeight: 400,
                    flexShrink: 0,
                  }}
                />
              ))}

              <Button
                variant="text"
                size="small"
                onClick={() => {
                  handleUnselectAllFiles();
                }}>
                <span className={classes.gradientText}>Clear all</span>
              </Button>
            </Box>
          )}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ padding: 0, overflow: "hidden", display: "flex" }}>
          <Grid
            container
            sx={{
              flex: 1,
            }}>
            <Grid
              item
              size={{
                md: showPreview ? 6 : 12,
                xs: 12,
              }}
              sx={{
                padding: 2,
                height: "100%",
                paddingBottom: 0,
                overflowY: "auto",
                borderRight: 1,
                borderColor: "divider",
              }}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}>
                <Box flex={1} overflow={"auto"}>
                  {filesData &&
                    filesData.files.map((file, index) => {
                      const { name, tags } = file;
                      const tagsList = tags
                        ? [tags.split(",")[0]].filter(
                            (tag) => tag && tag.trim() !== ""
                          )
                        : [];

                      const isSelected = selectedFiles.some(
                        (selectedFile) => selectedFile.id === file.id
                      );
                      return (
                        <Tooltip
                          key={index}
                          title={name}
                          arrow
                          slotProps={{
                            popper: {
                              disablePortal: true,
                              sx: {
                                "&[data-popper-reference-hidden]": {
                                  visibility: "hidden",
                                  "pointer-events": "none",
                                },
                              },
                            },
                          }}>
                          <MenuItem
                            onClick={() => handleFileCheckbox(isSelected, file)}
                            sx={{
                              paddingX: 2,
                              borderRadius: 1,
                              paddingLeft: 0,
                            }}>
                            <ListItemIcon>
                              <Checkbox
                                checked={isSelected}
                                sx={{
                                  "&:hover": { background: "transparent" },
                                  "&.Mui-checked": {
                                    color: theme.palette.text.primary,
                                  },
                                }}
                              />
                            </ListItemIcon>
                            <Box
                              width={"calc(100% - 36px)"}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  minWidth: 0,
                                  flex: 1,
                                }}>
                                <img
                                  src={getFileIconImage(file.file_type)}
                                  alt="file_icon"
                                  width={16}
                                  height={16}
                                />
                                <Box mr={1} />
                                <Typography
                                  noWrap
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    // maxWidth: 280,
                                  }}>
                                  {name}
                                </Typography>
                              </Box>
                              {tagsList.length > 0 && (
                                <Box
                                  display={"flex"}
                                  gap={1}
                                  ml={1.5}
                                  flexShrink={0}>
                                  {tagsList.slice(0, 2).map((tag) => (
                                    <Chip
                                      size="small"
                                      key={tag}
                                      label={
                                        tag[0].toUpperCase() + tag.slice(1)
                                      }
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        </Tooltip>
                      );
                    })}
                </Box>
                {filesData?.total > 0 && (
                  <TablePagination
                    component={Box}
                    count={filesData?.total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={50}
                    rowsPerPageOptions={[50]}
                  />
                )}
              </Box>
            </Grid>
            {showPreview && (
              <Grid
                item
                size={{
                  md: showPreview ? 6 : 0,
                  xs: 12,
                }}
                sx={{
                  height: "100%",
                  overflowY: "auto",
                  padding: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}>
                {selectedFiles.length > 0 ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}>
                    {/* Background stacked papers */}
                    {[...Array(Math.min(3, selectedFiles.length - 1))].map(
                      (_, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "absolute",
                            width: 288,
                            height: 400,
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            boxShadow: 1,
                            transform: `rotate(${index % 2 === 0 ? 4 : -4}deg)`,
                            zIndex: 3 - index,
                          }}
                        />
                      )
                    )}

                    {/* Top PDF preview */}
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 4,
                      }}>
                      {selectedFiles[0].file_type === "image" ? (
                        <ImagePreview file={selectedFiles[0]} />
                      ) : selectedFiles[0].file_type === "pdf" ? (
                        <PDFPreview file={selectedFiles[0]} />
                      ) : (
                        <NoPreview />
                      )}
                    </Box>

                    {selectedFiles.length > 1 && (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          position: "absolute",
                          bottom: -30,
                          left: "50%",
                          transform: "translateX(-50%)",
                          zIndex: 5,
                        }}>
                        {selectedFiles.length} files selected
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"center"}>
                    <img
                      src="/images/preview_image.png"
                      alt="preview"
                      style={{
                        maxWidth: 288,
                      }}
                    />
                    <Typography variant="subtitle2">
                      Please select a file to preview.
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => handleClose("refine-search")}>
            <span className={classes.gradientText}>Cancel</span>
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSelectButton("refine-search")}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function NoPreview() {
  return (
    <Box
      sx={{
        maxWidth: 288,
        width: "100%",
        position: "relative",
        background: "background.paper",
        borderRadius: 1,
      }}>
      <img src="/images/preview_image.png" alt="preview" width={"100%"} />
      <Typography
        variant="subtitle2"
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          textAlign: "center",
        }}>
        No preview available
      </Typography>
    </Box>
  );
}

function ImagePreview({ file }) {
  return <img src={file["file_url"]} alt="preview" width={288} />;
}

function PDFPreview({ file }) {
  return (
    <Document
      className={"documentBackground"}
      file={file["file_url"]}
      loading={
        <Box
          sx={{
            width: 288,
            height: 400,
            position: "relative",
            background: "background.paper",
            borderRadius: 1,
          }}>
          <Loader />
        </Box>
      }>
      <Page
        className={"canvasBackground"}
        pageNumber={1}
        width={288}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </Document>
  );
}

function OverflowTypography({ children, ...props }) {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [children]);

  return (
    <Tooltip title={children} arrow disableHoverListener={!isOverflowing}>
      <Typography
        ref={textRef}
        noWrap
        {...props}
        sx={{ ...props.sx, marginRight: 1 }}>
        {children}
      </Typography>
    </Tooltip>
  );
}
